// Writing section: TWO blocks.
//   1. "From the blog" — live Ghost feed (or empty state)
//   2. "Elsewhere" — curated cross-publication writing
// All copy passed via props. UI knows nothing about content.

import { Card, PostThumbnail, Section, detectOutlet } from '@ui/library';
import type { Post } from '@lib/schemas/post';
import { cx } from '@lib/utils/classNames';

export interface WritingBlogCopy {
  eyebrow: string;
  title: string;
  readMoreLabel: string;
  statusLive: string;
  statusFallback: string;
  statusLoading: string;
  emptyLoading: string;
  emptyDefault: string;
}

export interface WritingElsewhereCopy {
  eyebrow: string;
  title: string;
  lead: string;
}

export interface WritingCopy {
  blog: WritingBlogCopy;
  elsewhere: WritingElsewhereCopy;
}

export interface WritingProps {
  blogPosts: ReadonlyArray<Post>;
  externalPosts: ReadonlyArray<Post>;
  isLive: boolean;
  loading: boolean;
  blogHref: string;
  copy: WritingCopy;
}

function blogStatusText(
  loading: boolean,
  isLive: boolean,
  copy: WritingBlogCopy
): string {
  if (loading) return copy.statusLoading;
  if (isLive) return copy.statusLive;
  return copy.statusFallback;
}

interface PostGridProps {
  posts: ReadonlyArray<Post>;
}

function PostGrid({ posts }: PostGridProps) {
  return (
    <div className="posts" style={{ marginTop: 24 }}>
      {posts.map((p, i) => {
        const outlet = detectOutlet(p.href);
        return (
          <Card key={`${p.href}-${p.title}`} variant="post" href={p.href} staggerIndex={i}>
            <PostThumbnail
              {...(p.thumbnail ? { src: p.thumbnail } : {})}
              tag={p.tag}
              title={p.title}
              {...(outlet ? { outlet } : {})}
              className="post__thumb"
            />
            <span className="post__tag">{p.tag}</span>
            <h3>{p.title}</h3>
            <p className="post__excerpt">{p.excerpt}</p>
            <div className="post__foot">
              <span>{p.date}</span>
              {p.read && (
                <>
                  <span className="dot" />
                  <span>{p.read} read</span>
                </>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export function Writing({
  blogPosts,
  externalPosts,
  isLive,
  loading,
  blogHref,
  copy,
}: WritingProps) {
  const hasBlogPosts = blogPosts.length > 0;

  return (
    <>
      <Section
        id="writing"
        eyebrow={copy.blog.eyebrow}
        title={copy.blog.title}
        link={{ label: copy.blog.readMoreLabel, href: blogHref }}
      >
        <div className="ghost__status reveal">
          <span className={cx('ghost__dot', isLive && 'on')} />
          {blogStatusText(loading, isLive, copy.blog)}
        </div>
        {hasBlogPosts ? (
          <PostGrid posts={blogPosts} />
        ) : (
          <p className="reveal" style={{ marginTop: 24, color: 'var(--aw-ink-2)' }}>
            {loading ? copy.blog.emptyLoading : copy.blog.emptyDefault}
          </p>
        )}
      </Section>

      <Section
        id="writing-elsewhere"
        eyebrow={copy.elsewhere.eyebrow}
        title={copy.elsewhere.title}
        lead={copy.elsewhere.lead}
      >
        <PostGrid posts={externalPosts} />
      </Section>
    </>
  );
}
